/**
 * Phase 7-lite — iOS Add-to-Home-Screen 3-slide walkthrough modal.
 *
 * Visuals iterate v2: wraps each Safari mockup in a realistic iPhone bezel
 * so the illustration reads as a real device screenshot at a glance. Each
 * step's title is an imperative action ("TAP YOUR SHARE BUTTON") with
 * minimal supporting copy — the visual carries the meaning.
 *
 * Three variants of Safari layout are supported (iPhone bottom toolbar /
 * iPhone compact ⋯ menu / iPad top toolbar) with auto-detection from UA
 * and a "My screen looks different" expander to switch.
 */
import { useEffect, useState } from 'react';
import { detectPlatform } from '../utils/installPwa';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// ─── iOS Share icon (square + arrow-up) — pixel-accurate to Apple's SF Symbol
const ShareIcon = ({ size = 28, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="none">
    <path
      d="M12 3.5v12M12 3.5l-3.5 3.5M12 3.5l3.5 3.5"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
    />
    <path
      d="M6 11.5H4.5A1.5 1.5 0 0 0 3 13v6a1.5 1.5 0 0 0 1.5 1.5h15A1.5 1.5 0 0 0 21 19v-6a1.5 1.5 0 0 0-1.5-1.5H18"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
    />
  </svg>
);

// ─── Animated finger/tap pointer — bouncing dot with rippling rings.
// Communicates "tap here" via continuous motion (not just a static glow).
const TapPointer = ({ className = '' }) => (
  <div className={`absolute pointer-events-none ${className}`}>
    <div className="relative w-10 h-10" style={{ animation: 'amTapBounce 1.4s ease-in-out infinite' }}>
      {/* Outer rippling ring */}
      <div
        className="absolute inset-0 rounded-full border-2 border-[#FF9F0A]"
        style={{ animation: 'amTapRipple 1.4s ease-out infinite' }}
      />
      {/* Inner rippling ring (offset for layered effect) */}
      <div
        className="absolute inset-0 rounded-full border-2 border-[#FF9F0A]"
        style={{ animation: 'amTapRipple 1.4s ease-out 0.5s infinite' }}
      />
      {/* Solid center dot — the "fingertip" */}
      <div className="absolute inset-2.5 rounded-full bg-[#FF9F0A] shadow-[0_0_14px_rgba(255,159,10,0.9)]" />
    </div>
  </div>
);

// ─── iPhone bezel wrapper — makes the inner Safari mockup read as a real
// device screenshot. Approximates an iPhone 14/15 with Dynamic Island.
function PhoneFrame({ children, isIPad = false }) {
  if (isIPad) {
    // iPad bezel — thinner, more square
    return (
      <div className="mx-auto" style={{ maxWidth: 360 }}>
        <div className="bg-[#1C1C1E] rounded-[28px] p-2 shadow-[0_10px_40px_rgba(0,0,0,0.6)] border border-[#3A3A3C]">
          <div className="rounded-[20px] overflow-hidden bg-black">
            {children}
          </div>
        </div>
      </div>
    );
  }
  // iPhone bezel — rounded with Dynamic Island
  return (
    <div className="mx-auto relative" style={{ maxWidth: 270 }}>
      <div className="bg-black rounded-[40px] p-1.5 shadow-[0_10px_40px_rgba(0,0,0,0.8)] border-2 border-[#2C2C2E]">
        {/* Dynamic island */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-10" />
        <div className="rounded-[32px] overflow-hidden bg-black">
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Safari Bottom Toolbar mockup (iOS 16-18 iPhone default)
function SafariBottomToolbar() {
  return (
    <PhoneFrame>
      <div className="bg-[#0C0612]" data-testid="safari-bottom-mockup">
        {/* Status bar (time + battery) */}
        <div className="bg-black px-5 pt-7 pb-1 flex items-center justify-between">
          <span className="text-white text-[10px] font-semibold">9:41</span>
          <span className="text-white text-[10px]">●●● ▮</span>
        </div>
        {/* URL bar */}
        <div className="bg-[#1C1C1E] px-3 py-2 flex items-center gap-2 border-b border-black/50">
          <div className="text-[10px] text-[#8E8E93] font-mono flex-1 text-center">🔒 ashandmerch.com</div>
        </div>
        {/* page content placeholder */}
        <div className="bg-[#050003] h-28 flex items-center justify-center">
          <div className="text-white/30 text-[10px]">Ash &amp; Merch</div>
        </div>
        {/* BOTTOM TOOLBAR — the target */}
        <div className="bg-[#1C1C1E] px-4 py-3 pb-4 flex items-center justify-between border-t border-black/50 relative">
          <span className="text-[#8E8E93] text-xl leading-none">‹</span>
          <span className="text-[#8E8E93] text-xl leading-none">›</span>
          <div className="relative" data-testid="share-highlight">
            <div className="absolute -inset-2.5 rounded-full border-2 border-[#FF9F0A] animate-pulse shadow-[0_0_20px_rgba(255,159,10,0.9)]" />
            <ShareIcon size={22} className="text-white relative z-10" />
            <TapPointer className="-right-9 -top-1" />
          </div>
          <span className="text-[#8E8E93] text-lg leading-none">⎕</span>
          <span className="text-[#8E8E93] text-lg leading-none">≡</span>
        </div>
        {/* Home indicator */}
        <div className="bg-black flex justify-center py-1">
          <div className="w-24 h-1 bg-white/40 rounded-full" />
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── Safari with `⋯` overflow menu variant (iOS 17+ compact)
function SafariCompactMenu() {
  return (
    <PhoneFrame>
      <div className="bg-[#0C0612]" data-testid="safari-compact-mockup">
        <div className="bg-black px-5 pt-7 pb-1 flex items-center justify-between">
          <span className="text-white text-[10px] font-semibold">9:41</span>
          <span className="text-white text-[10px]">●●● ▮</span>
        </div>
        <div className="bg-[#1C1C1E] px-3 py-2.5 flex items-center gap-2 border-b border-black/50 relative">
          <div className="text-[10px] text-[#8E8E93] font-mono flex-1">🔒 ashandmerch.com</div>
          <span className="text-[#8E8E93] text-xs">AA</span>
          <div className="relative" data-testid="share-highlight">
            <div className="absolute -inset-2 rounded-full border-2 border-[#FF9F0A] animate-pulse shadow-[0_0_18px_rgba(255,159,10,0.9)]" />
            <span className="text-white font-bold text-sm relative z-10">⋯</span>
            <TapPointer className="-right-9 -top-1" />
          </div>
        </div>
        <div className="bg-[#050003] h-36 flex items-center justify-center">
          <div className="text-white/30 text-[10px]">Ash &amp; Merch</div>
        </div>
        <div className="bg-black flex justify-center py-1">
          <div className="w-24 h-1 bg-white/40 rounded-full" />
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── Safari iPad / top toolbar variant
function SafariTopToolbar() {
  return (
    <PhoneFrame isIPad>
      <div className="bg-[#0C0612]" data-testid="safari-top-mockup">
        <div className="bg-[#1C1C1E] px-3 py-2 flex items-center gap-2 border-b border-black/50 relative">
          <span className="text-[#8E8E93] text-base">‹ ›</span>
          <div className="flex-1 bg-[#050003] rounded px-2 py-1">
            <div className="text-[10px] text-[#8E8E93] font-mono">🔒 ashandmerch.com</div>
          </div>
          <div className="relative" data-testid="share-highlight">
            <div className="absolute -inset-2 rounded-full border-2 border-[#FF9F0A] animate-pulse shadow-[0_0_18px_rgba(255,159,10,0.9)]" />
            <ShareIcon size={18} className="text-white relative z-10" />
            <TapPointer className="-right-9 -top-1" />
          </div>
          <span className="text-[#8E8E93] text-base">⎕</span>
        </div>
        <div className="bg-[#050003] h-24 flex items-center justify-center">
          <div className="text-white/30 text-[10px]">Ash &amp; Merch</div>
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── iOS Action Sheet mockup with "Add to Home Screen" highlighted
function ActionSheetMockup() {
  return (
    <PhoneFrame>
      <div className="bg-[#0C0612]" data-testid="action-sheet-mockup">
        <div className="bg-black px-5 pt-7 pb-1 flex items-center justify-between">
          <span className="text-white text-[10px] font-semibold">9:41</span>
          <span className="text-white text-[10px]">●●● ▮</span>
        </div>
        {/* Sheet header */}
        <div className="bg-[#2C2C2E] px-3 py-2 border-b border-black/50 flex items-center justify-between">
          <div className="text-[10px] text-[#8E8E93] font-mono">Ash &amp; Merch</div>
          <div className="text-[10px] text-[#0A84FF] font-semibold">Done</div>
        </div>
        <div className="bg-[#1C1C1E] py-2">
          <div className="px-4 py-2 text-white text-xs opacity-50 flex items-center justify-between">
            <span>AirDrop</span><span className="text-[#8E8E93]">⇪</span>
          </div>
          <div className="px-4 py-2 text-white text-xs opacity-50 flex items-center justify-between">
            <span>Copy</span><span className="text-[#8E8E93]">⎘</span>
          </div>
          {/* THE TARGET */}
          <div className="mx-2 my-1 px-3 py-3 bg-[#FF9F0A]/15 border-2 border-[#FF9F0A] rounded-lg flex items-center justify-between relative animate-pulse">
            <span className="text-white text-xs font-bold">Add to Home Screen</span>
            <span className="text-[#FF9F0A] text-base">⊕</span>
            <TapPointer className="-right-3 top-1/2 -translate-y-1/2" />
          </div>
          <div className="px-4 py-2 text-white text-xs opacity-50 flex items-center justify-between">
            <span>Add Bookmark</span><span className="text-[#8E8E93]">📑</span>
          </div>
          <div className="px-4 py-2 text-white text-xs opacity-50 flex items-center justify-between">
            <span>Find on Page</span><span className="text-[#8E8E93]">🔍</span>
          </div>
        </div>
        <div className="bg-black flex justify-center py-1">
          <div className="w-24 h-1 bg-white/40 rounded-full" />
        </div>
      </div>
    </PhoneFrame>
  );
}

// ─── Add to Home Screen confirmation page mockup
function ConfirmAddMockup() {
  return (
    <PhoneFrame>
      <div className="bg-[#0C0612]" data-testid="confirm-add-mockup">
        <div className="bg-black px-5 pt-7 pb-1 flex items-center justify-between">
          <span className="text-white text-[10px] font-semibold">9:41</span>
          <span className="text-white text-[10px]">●●● ▮</span>
        </div>
        <div className="bg-[#2C2C2E] px-3 py-2 border-b border-black/50 flex items-center justify-between">
          <div className="text-[11px] text-[#0A84FF]">Cancel</div>
          <div className="text-[11px] text-white font-semibold">Add to Home Screen</div>
          <div className="relative">
            <div className="absolute -inset-2 rounded-md border-2 border-[#FF9F0A] animate-pulse shadow-[0_0_18px_rgba(255,159,10,0.9)]" />
            <div className="text-[11px] text-white font-bold bg-[#0A84FF] px-2 py-0.5 rounded relative z-10">Add</div>
            <TapPointer className="-right-9 -top-1" />
          </div>
        </div>
        <div className="bg-[#1C1C1E] p-4 flex items-center gap-3">
          {/* Real Ash & Merch PWA icon (same file iOS will use after install) */}
          <img
            src="/icon-192.png"
            alt=""
            className="w-14 h-14 rounded-xl shrink-0"
            style={{ imageRendering: 'auto' }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-semibold">Ash &amp; Merch</div>
            <div className="text-[10px] text-[#8E8E93] truncate">ashandmerch.com</div>
          </div>
        </div>
        <div className="bg-[#050003] h-12 flex items-center justify-center">
          <div className="text-white/30 text-[9px]">Will appear on your home screen</div>
        </div>
        <div className="bg-black flex justify-center py-1">
          <div className="w-24 h-1 bg-white/40 rounded-full" />
        </div>
      </div>
    </PhoneFrame>
  );
}

const VARIANTS = [
  { key: 'bottom_toolbar', label: 'iPhone (most common)', Component: SafariBottomToolbar, hint: 'Look at the BOTTOM of your phone screen.' },
  { key: 'compact_menu',   label: 'iPhone with ⋯ menu',  Component: SafariCompactMenu,   hint: 'Look at the URL bar — find the three dots, then Share.' },
  { key: 'top_toolbar',    label: 'iPad / top toolbar',  Component: SafariTopToolbar,    hint: 'Look at the TOP-RIGHT of your iPad screen.' },
];

export function PWAInstallWalkthrough({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [variant, setVariant] = useState('bottom_toolbar');
  const [showVariants, setShowVariants] = useState(false);
  const { isModern } = useTheme();

  useEffect(() => {
    if (!open) return;
    const { isIPad } = detectPlatform();
    setVariant(isIPad ? 'top_toolbar' : 'bottom_toolbar');
    setStep(0);
    setShowVariants(false);
  }, [open]);

  if (!open) return null;

  const currentVariant = VARIANTS.find((v) => v.key === variant) || VARIANTS[0];
  const VariantComponent = currentVariant.Component;

  const slides = [
    {
      title: 'TAP YOUR SHARE BUTTON',
      mtitle: 'Tap your Share button',
      hint: currentVariant.hint,
      body: (
        <>
          <VariantComponent />
          <div className="mt-5 text-center">
            <button
              onClick={() => setShowVariants((s) => !s)}
              className={isModern ? "text-sm underline text-[var(--am-ink-2)] hover:text-[var(--am-emerald-700)]" : "text-[#A3C7A8] font-mono text-xs underline hover:text-[#39FF14]"}
              data-testid="show-variants-toggle"
            >
              {showVariants ? '← back' : 'My screen looks different →'}
            </button>
            {showVariants && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {VARIANTS.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => { setVariant(v.key); setShowVariants(false); }}
                    className={isModern
                      ? `p-2 rounded-[10px] border text-xs text-left ${variant === v.key ? 'border-[var(--am-emerald-500)] bg-[var(--am-emerald-50)] text-[var(--am-emerald-800)]' : 'border-[var(--am-hair-strong)] text-[var(--am-ink-2)]'}`
                      : `p-2 border font-mono text-[10px] text-left ${
                      variant === v.key
                        ? 'border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]'
                        : 'border-[#4B6B52] text-[#A3C7A8] hover:border-[#39FF14]'
                    }`}
                    data-testid={`variant-${v.key}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ),
    },
    {
      title: 'TAP ADD TO HOME SCREEN',
      mtitle: 'Tap Add to Home Screen',
      hint: 'A menu will appear. Scroll down if needed.',
      body: <ActionSheetMockup />,
    },
    {
      title: 'TAP ADD',
      mtitle: 'Tap Add',
      hint: 'Confirms the install. The app icon appears on your home screen.',
      body: (
        <>
          <ConfirmAddMockup />
          <div className={isModern ? 'mt-5 p-3 rounded-[10px] bg-[var(--am-emerald-50)] border border-[var(--am-emerald-200)]' : 'mt-5 p-3 border border-[#39FF14]/40 bg-[#39FF14]/5'}>
            <div className="flex items-start gap-2">
              <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isModern ? 'text-[var(--am-emerald-600)]' : 'text-[#39FF14]'}`} />
              <div className={isModern ? 'text-sm text-[var(--am-emerald-800)] leading-relaxed' : 'font-mono text-xs text-[#A3C7A8] leading-relaxed'}>
                Open the app <span className={isModern ? 'font-semibold' : 'text-[#39FF14]'}>from the home screen icon</span>, not from Safari, to receive push alerts.
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  const current = slides[step];

  if (isModern) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="pwa-install-walkthrough" style={{ fontFamily: 'var(--am-font-sans)' }}>
        <div className="am-modern-scope bg-white w-full max-w-lg max-h-[92dvh] overflow-y-auto rounded-[20px]" style={{ boxShadow: 'var(--am-shadow-3)' }} onClick={(e) => e.stopPropagation()}>
          <div className="p-5 border-b sticky top-0 bg-white z-10" style={{ borderColor: 'var(--am-hair)' }}>
            <div className="flex items-center justify-between">
              <span className="am-label">Step {step + 1} of 3</span>
              <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[var(--am-inset)]" style={{ color: 'var(--am-ink-2)' }} aria-label="Close" data-testid="walkthrough-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <h2 className="text-xl font-semibold mt-1" style={{ color: 'var(--am-ink-1)' }}>{current.mtitle}</h2>
            {current.hint && <p className="text-sm mt-1" style={{ color: 'var(--am-ink-3)' }}>{current.hint}</p>}
          </div>
          <div className="px-5 pt-6 pb-3">{current.body}</div>
          <div className="flex items-center justify-center gap-2 my-3">
            {slides.map((_, i) => (
              <div key={i} className="h-1.5 rounded-full transition-all" style={{ width: i === step ? 32 : 6, background: i === step ? 'var(--am-emerald-600)' : 'var(--am-hair-strong)' }} />
            ))}
          </div>
          <div className="flex items-center justify-between p-4 border-t sticky bottom-0 bg-white" style={{ borderColor: 'var(--am-hair)' }}>
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="am-btn-ghost disabled:opacity-30" data-testid="walkthrough-prev">
              <ChevronLeft className="w-4 h-4" />Back
            </button>
            {step < slides.length - 1 ? (
              <button onClick={() => setStep((s) => s + 1)} className="am-cta px-5 h-10 text-sm inline-flex items-center gap-1" data-testid="walkthrough-next">
                Next<ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={onClose} className="am-cta px-5 h-10 text-sm inline-flex items-center gap-1" data-testid="walkthrough-done">
                <Check className="w-4 h-4" />Got it
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="pwa-install-walkthrough"
    >
      <div
        className="bg-[#050003] border border-[#39FF14] w-full max-w-lg max-h-[92dvh] overflow-y-auto shadow-[0_0_60px_rgba(57,255,20,0.3)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: step count + huge action verb */}
        <div className="p-4 border-b border-[#39FF14]/30 sticky top-0 bg-[#050003] z-10">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[#4B6B52] text-[10px] tracking-widest">
              STEP {step + 1} OF 3
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-[#A3C7A8] hover:text-[#FF3B30]"
              aria-label="Close"
              data-testid="walkthrough-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <h2 className="font-mono text-[#39FF14] text-lg sm:text-xl font-bold tracking-tight mt-1 leading-tight">
            {current.title}
          </h2>
          {current.hint && (
            <p className="font-mono text-[#A3C7A8] text-xs mt-1.5 opacity-80">
              {current.hint}
            </p>
          )}
        </div>

        {/* Body — visual carries the meaning */}
        <div className="px-5 pt-6 pb-3">{current.body}</div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-2 my-3">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-8 bg-[#39FF14]' : 'w-1.5 bg-[#4B6B52]'
              }`}
            />
          ))}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between p-4 border-t border-[#39FF14]/30 bg-[#0C0612] sticky bottom-0">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="flex items-center gap-1 font-mono text-xs text-[#A3C7A8] disabled:opacity-30 hover:text-[#39FF14] px-3 py-2"
            data-testid="walkthrough-prev"
          >
            <ChevronLeft className="w-4 h-4" /> BACK
          </button>
          {step < slides.length - 1 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="flex items-center gap-1 font-mono text-xs bg-[#39FF14] text-[#050003] px-4 py-2 font-bold hover:bg-[#4AF626]"
              data-testid="walkthrough-next"
            >
              NEXT <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex items-center gap-1 font-mono text-xs bg-[#39FF14] text-[#050003] px-4 py-2 font-bold hover:bg-[#4AF626]"
              data-testid="walkthrough-done"
            >
              <Check className="w-4 h-4" /> GOT IT
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PWAInstallWalkthrough;
